import { useState, useEffect, useRef } from 'react'
import { deleteModel, createModelWithCredentials, updateModel, connectModelToOnlyfansapi, connectModelOnlyfansApiAccount, cancelModelConnection, removeModelProvider, subscribeConnectionStatus } from '../api'
import { useModelsList } from '../hooks/useModelsList'
import AccountUsageIndicator from '../components/AccountUsageIndicator'
import EntityCard from '../components/EntityCard'
import ConfirmModal from '../components/ConfirmModal'
import ConnectModal from '../components/ConnectModal'
import TwoFAModal from '../components/TwoFAModal'
import ModelProviderSlotRow from './Models/ModelProviderSlotRow'
import { IconLink, IconEye, IconTrash } from '../components/ui/Icons'
import { PageSection } from '../components/ui'
import { useI18n } from '../i18n'
import { useToast } from '../contexts/ToastContext'
import { getApiErrorMessage } from '../api'
import '../styles/pages/models.css'
import '../styles/pages/page-common.css'

function hasAnyConnecting(models) {
  return models.some((m) =>
    (m.provider_links || []).some((l) => l.connection_status === 'connecting')
  )
}

export default function Models() {
  const { t } = useI18n()
  const { addToast } = useToast()
  const { models, setModels, loading, error, setError, load, refresh } = useModelsList()
  const [modelToDelete, setModelToDelete] = useState(null)
  const unsubscribeRef = useRef(null)

  const [deleting, setDeleting] = useState(false)
  const [connectionModalModel, setConnectionModalModel] = useState(null)
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [createError, setCreateError] = useState('')
  const [onlyFansApiModalModel, setOnlyFansApiModalModel] = useState(null)
  const [connectionErrorByModelId, setConnectionErrorByModelId] = useState({})
  const [connectingModelId, setConnectingModelId] = useState(null)
  const [cancellingModelId, setCancellingModelId] = useState(null)
  const [cancellingProvider, setCancellingProvider] = useState(null)
  const [disconnectingLink, setDisconnectingLink] = useState(null) // { modelId, linkId }
  const [disconnectConfirmLink, setDisconnectConfirmLink] = useState(null) // { modelId, linkId, modelName, providerLabel }
  /** true после нажатия «Подключить» — держим подписку на SSE, чтобы не пропустить результат */
  const [connectionPending, setConnectionPending] = useState(false)
  const hadConnectingRef = useRef(false)

  // Сбрасываем ожидание только когда список перешёл из «есть connecting» в «нет connecting» (результат подтянулся)
  useEffect(() => {
    const connecting = hasAnyConnecting(models)
    if (connecting) hadConnectingRef.current = true
    if (!connecting && hadConnectingRef.current && connectionPending) {
      setConnectionPending(false)
      hadConnectingRef.current = false
    }
  }, [models, connectionPending])

  // Подписка на SSE: когда есть «connecting» ИЛИ мы только отправили запрос (connectionPending)
  useEffect(() => {
    const shouldSubscribe = hasAnyConnecting(models) || connectionPending
    if (!shouldSubscribe) {
      if (unsubscribeRef.current) {
        unsubscribeRef.current()
        unsubscribeRef.current = null
      }
      return
    }
    unsubscribeRef.current = subscribeConnectionStatus(() => {
      refresh().then(() => setConnectionPending(false))
    })
    return () => {
      if (unsubscribeRef.current) {
        unsubscribeRef.current()
        unsubscribeRef.current = null
      }
    }
  }, [models, connectionPending, load])

  // Таймаут ожидания 2 мин — если событие так и не пришло, сбрасываем подписку
  useEffect(() => {
    if (!connectionPending) return
    const t = setTimeout(() => setConnectionPending(false), 120000)
    return () => clearTimeout(t)
  }, [connectionPending])

  async function handleConfirmDelete() {
    if (!modelToDelete) return
    setDeleting(true)
    setError('')
    try {
      await deleteModel(modelToDelete.id)
      setModelToDelete(null)
      load()
      addToast(t('toast.model.deleted'), 'success')
    } catch (err) {
      const msg = getApiErrorMessage(err, t('common.error'))
      setError(msg)
    } finally {
      setDeleting(false)
    }
  }

  async function handleConnectOnlyFansApi(modelId, options = null) {
    const twoFa = typeof options === 'string' ? options : options?.twoFa
    const apiKey = typeof options === 'object' ? options?.apiKey : ''
    const accountId = typeof options === 'object' ? options?.accountId : ''
    setConnectionErrorByModelId((prev) => {
      const next = { ...prev }
      delete next[modelId]
      return next
    })
    setOnlyFansApiModalModel(null)
    setConnectingModelId(modelId)
    setConnectionPending(true)
    try {
      if ((apiKey || '').trim() || (accountId || '').trim()) {
        const updated = await connectModelOnlyfansApiAccount(modelId, {
          apiKey: (apiKey || '').trim() || undefined,
          accountId: (accountId || '').trim() || undefined,
        })
        setModels((prev) => prev.map((model) => (model.id === updated.id ? updated : model)))
      } else {
        await connectModelToOnlyfansapi(modelId, { two_fa: twoFa || undefined })
      }
      await refresh()
      addToast(t('toast.connection.started'), 'success')
    } catch (err) {
      const msg = getApiErrorMessage(err, t('common.error'))
      setConnectionErrorByModelId((prev) => ({ ...prev, [modelId]: msg }))
      setConnectionPending(false)
    } finally {
      setConnectingModelId(null)
    }
  }

  async function handleCancelConnection(modelId, provider) {
    setCancellingModelId(modelId)
    setCancellingProvider(provider ?? null)
    setConnectionErrorByModelId((prev) => {
      const next = { ...prev }
      delete next[modelId]
      return next
    })
    try {
      await cancelModelConnection(modelId, provider != null ? { provider } : {})
      await refresh()
      setConnectionPending(false)
      addToast(t('toast.connection.cancelled'), 'success')
    } catch (err) {
      const msg = getApiErrorMessage(err, t('common.error'))
      setConnectionErrorByModelId((prev) => ({ ...prev, [modelId]: msg }))
    } finally {
      setCancellingModelId(null)
      setCancellingProvider(null)
    }
  }

  async function handleDisconnectProvider(modelId, linkId) {
    setDisconnectingLink({ modelId, linkId })
    setError('')
    try {
      await removeModelProvider(modelId, linkId)
      await refresh()
      addToast(t('toast.provider.disconnected'), 'success')
    } catch (err) {
      const msg = getApiErrorMessage(err, t('common.error'))
      setError(msg)
    } finally {
      setDisconnectingLink(null)
    }
  }

  function openOnlyFansApi2FAModal(model) {
    setConnectionErrorByModelId((prev) => {
      const next = { ...prev }
      delete next[model.id]
      return next
    })
    setOnlyFansApiModalModel(model)
  }

  function handleSaveFromModal(creds, mode, model) {
    if (mode === 'create') {
      setCreateError('')
      return createModelWithCredentials(creds)
        .then((created) => {
          setModels((prev) => [...prev, created])
          addToast(t('toast.model.created'), 'success')
          return load()
        })
        .catch((err) => {
          const msg = getApiErrorMessage(err, t('common.error'))
          setCreateError(msg)
          throw err
        })
    }
    if (mode === 'edit' && model?.id) {
      return updateModel(model.id, { credentials: creds })
        .then(() => {
          addToast(t('toast.credentials.saved'), 'success')
          return load()
        })
        .catch((err) => {
          const msg = getApiErrorMessage(err, t('common.error'))
          setCreateError(msg)
          throw err
        })
    }
    return Promise.resolve()
  }

  return (
    <div className="models-page">
      <PageSection
        title={t('models.title')}
        actions={
          <button
            type="button"
            className="btn-primary"
            onClick={() => { setShowCreateModal(true); setCreateError('') }}
          >
            {t('models.add')}
          </button>
        }
      >
      {error && <div className="models-error">{error}</div>}
      {createError && (
        <div className="models-error models-create-error">
          {createError}
          <button
            type="button"
            className="models-retry-connect"
            onClick={() => { setShowCreateModal(true); setCreateError('') }}
          >
            {t('common.retry')}
          </button>
        </div>
      )}

      {loading ? (
        <p className="models-loading">{t('models.loading')}</p>
      ) : models.length === 0 ? (
        <p className="models-empty">{t('models.empty')}</p>
      ) : (
        <div className="models-grid">
          {models.map((m) => {
            const connected = m.provider_links?.some((l) => l.connection_status === 'connected')
            const slots = (m.provider_links || []).filter((l) => l.provider === 'onlyfansapi')
            const accountUsageOk = !!m.is_account_ready_for_use
            const accountUsageTitle = accountUsageOk
              ? t('models.account.ok')
              : t('models.account.bad.prefix') +
                (() => {
                  const hasDesc = (m.account_description || '').trim()
                  const hasTz = (m.timezone || '').trim()
                  if (!hasDesc && !hasTz) return t('models.account.bad.both')
                  if (!hasDesc) return t('models.account.bad.description')
                  return t('models.account.bad.timezone')
                })()
            return (
              <EntityCard
                key={m.id}
                leading={
                  <AccountUsageIndicator
                    isReady={accountUsageOk}
                    title={accountUsageTitle}
                    className="model-card-usage-indicator"
                  />
                }
                title={m.of_email || m.name}
                titleHref={`/old/models/${m.id}`}
                actions={[
                  { icon: <IconLink />, title: t('models.connection.edit'), onClick: () => setConnectionModalModel(m), variant: 'accent' },
                  ...(connected
                    ? [
                        { icon: <IconEye />, title: t('models.connection.info'), href: `/old/models/${m.id}` },
                        { icon: <IconTrash />, title: t('models.connection.delete'), onClick: () => setModelToDelete(m), variant: 'danger' },
                      ]
                    : []),
                ]}
              >
                <div className="model-card-providers">
                  {slots.map((link) => (
                    <ModelProviderSlotRow
                      key={link.id != null ? link.id : link.provider}
                      link={link}
                      modelId={m.id}
                      modelName={m.of_email || m.name}
                      connectionError={connectionErrorByModelId[m.id]}
                      connectingModelId={connectingModelId}
                      cancellingModelId={cancellingModelId}
                      cancellingProvider={cancellingProvider}
                      disconnectingLink={disconnectingLink}
                      onConnect={() => openOnlyFansApi2FAModal(m)}
                      onCancelConnection={handleCancelConnection}
                      onDisconnectConfirm={setDisconnectConfirmLink}
                    />
                  ))}
                </div>
              </EntityCard>
            )
          })}
        </div>
      )}
      </PageSection>

      <ConfirmModal
        open={!!modelToDelete}
        title={t('models.delete.title')}
        message={
          modelToDelete
            ? t('models.delete.message', {
                name: modelToDelete.of_email || modelToDelete.name || t('common.notAvailable'),
              })
            : ''
        }
        confirmLabel={deleting ? t('common.deleting') : t('common.delete')}
        cancelLabel={t('common.cancel')}
        danger
        confirmDisabled={deleting}
        onConfirm={handleConfirmDelete}
        onCancel={() => !deleting && setModelToDelete(null)}
      />

      <ConfirmModal
        open={!!disconnectConfirmLink}
        title={t('models.disconnect.title')}
        message={
          disconnectConfirmLink
            ? t('models.disconnect.message', {
                provider: disconnectConfirmLink.providerLabel,
                model: disconnectConfirmLink.modelName,
              })
            : ''
        }
        confirmLabel={
          disconnectingLink ? t('models.disconnect.progress') : t('models.disconnect.confirm')
        }
        cancelLabel={t('common.cancel')}
        danger
        confirmDisabled={!!disconnectingLink}
        onConfirm={async () => {
          if (!disconnectConfirmLink) return
          try {
            await handleDisconnectProvider(disconnectConfirmLink.modelId, disconnectConfirmLink.linkId)
          } finally {
            setDisconnectConfirmLink(null)
          }
        }}
        onCancel={() => !disconnectingLink && setDisconnectConfirmLink(null)}
      />

      <ConnectModal
        open={!!connectionModalModel}
        mode="edit"
        model={connectionModalModel}
        onCancel={() => setConnectionModalModel(null)}
        onSave={(creds) => handleSaveFromModal(creds, 'edit', connectionModalModel)}
      />

      <ConnectModal
        open={showCreateModal}
        mode="create"
        model={null}
        onCancel={() => setShowCreateModal(false)}
        onSave={(creds) => handleSaveFromModal(creds, 'create', null)}
      />

      <TwoFAModal
        title={t('models.onlyfansapi.2fa.title')}
        titleId="onlyfansapi-2fa-modal-title"
        formId="onlyfansapi-2fa-modal-form"
        open={!!onlyFansApiModalModel}
        model={onlyFansApiModalModel}
        showOnlyFansApiAccountFields
        onConnect={(values) => onlyFansApiModalModel && handleConnectOnlyFansApi(onlyFansApiModalModel.id, values)}
        onCancel={() => setOnlyFansApiModalModel(null)}
      />
    </div>
  )
}
